import { IFormattedAssetItem } from '@northladder/services';

import { ApiClient } from '../api-client';

import {
  formatEvaluationsDetailsResponse,
  formatInboundFilterResponse,
  formatSourceDetailsResponse,
} from './clients/inbound/transformers';
import {
  IAddDeleteImageToEvaluationPayload,
  IBulkScanInventoriesPayload,
  IBulkScanInventoriesResponseRaw,
  ICloseEvaluationPayload,
  ICloseEvaluationResponse,
  ICloseInboundTransactionPayload,
  ICreateOrUpdateInboundPayload,
  ICreateOrUpdateInboundResponse,
  IEvaluationDetailsResponse,
  IEvaluationImageConfigurationResponse,
  IEvaluationsListResponse,
  IFetchEvaluationDetailsParams,
  IFetchEvaluationImagesConfigurationParams,
  IFetchEvaluationListParams,
  IFetchNlStaffUrlOptions,
  IFetchPodDetailUrlOptions,
  IFetchSearchListUrlOptions,
  IFieldToUpdateTypesResponseRaw,
  IGetInboundHistoryListPayload,
  IGetMissingInventoryListPayload,
  IGetPodInventoryListPayload,
  IInboundCourierProviderResponse,
  IInboundDetailResponseRaw,
  IInboundHistoryListResponseRaw,
  IInboundResponseRaw,
  IInboundSearchFilterOptionResponse,
  IInboundTransactionTypesResponse,
  IMissingInventoryListResponse,
  IMissingInventoryPayload,
  IMissingInventoryResponse,
  INlStaffResponse,
  IPodDetailsDataResponseRaw,
  IPodInventoryListResponseRaw,
  IScanEvaluatedAppraisalPayload,
  IScanPackageForEvaluationPayload,
  IScanPackageForEvaluationResponse,
  IScanPortForCreatingEvaluationPayload,
  IScanPortForCreatingEvaluationResponse,
  IScanPortForExistingEvaluationPayload,
  IScanPortForExistingEvaluationResponse,
  ISourceDetailsData,
  ISourceDetailsParams,
  ISourceDetailsResponseRaw,
  IUpdateInboundPackagePayload,
  IUpdateInboundPackageResponse,
  TInboundListResponse,
} from './types';
import {
  addOrDeleteMediaToEvaluationUrl,
  closeEvaluationUrl,
  closeInboundTransactionUrl,
  fetchCreateOrUpdateInboundUrl,
  fetchFieldToUpdateTypesListUrl,
  fetchInboundCourierProviderListUrl,
  fetchInboundDetailsInfoUrl,
  fetchInboundListFilterOptionsUrl,
  fetchInboundListUrl,
  fetchInboundStatusListUrl,
  fetchInboundTypesListUrl,
  fetchInventoryListFilterOptionsUrl,
  fetchMissingInventoryListUrl,
  fetchPodDetailsWithAgingInfoUrl,
  fetchPodInventoryListUrl,
  fetchUpdateInboundPackageUrl,
  getBulkScanInventoriesUrl,
  getEvaluationDetailsUrl,
  getEvaluationImageUploadConfigUrl,
  getEvaluationsList,
  getMarkInventoryAsMissingUrl,
  getNlStaffUrl,
  getSourceDetailsUrl,
  inboundHistoryDetailsListInfoUrl,
  scanEvaluatedAppraisalUrl,
  scanPackageForEvaluationUrl,
  scanPortForEvaluationUrl,
  scanPortForExistingEvaluationUrl,
} from './urls';

/**
 * This is provides the API client methods for all inbound page,
 * filters dropdowns and components for the inventory services.
 */
export class InboundAPIClient extends ApiClient {
  private static classInstance?: InboundAPIClient;

  private constructor() {
    super({
      apiBaseURL: 'INVENTORY_MANAGEMENT_SERVICE_BASE_URL',
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new InboundAPIClient();
    }

    return this.classInstance;
  };

  /**
   * Api call to fetch the the Northladder staff list data.
   * @returns INlStaffResponse
   */
  public fetchAllNlStaffs = async (queryParams: IFetchNlStaffUrlOptions) => {
    const response = await this.get<INlStaffResponse>(
      getNlStaffUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the the Inbound Details.
   * TODO: Delete this since it's not used.
   * @deprecated - Not used at all
   */
  public fetchInboundDetails = async (inboundIdentifier: string) => {
    const response = await this.get<IInboundDetailResponseRaw>(
      fetchInboundDetailsInfoUrl(inboundIdentifier)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the the Inbound List Items.
   * @returns TInboundListResponse
   */
  public fetchInboundList = async (queryParams: IFetchSearchListUrlOptions) => {
    const response = await this.get<TInboundListResponse>(
      fetchInboundListUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the the Inbound List Filter Options.
   * @returns IInboundSearchFilterOptionResponse
   */
  public fetchInboundFilterListOptions = async () => {
    const response = await this.get<IInboundSearchFilterOptionResponse>(
      fetchInboundListFilterOptionsUrl()
    );

    if (!response.success) throw response;

    return formatInboundFilterResponse(response.data.data);
  };

  /**
   * Api call to fetch the the Inventory List Filter Options.
   * @returns IInboundSearchFilterOptionResponse
   */
  public fetchInventoryFilterListOptions = async () => {
    const response = await this.get<IInboundSearchFilterOptionResponse>(
      fetchInventoryListFilterOptionsUrl()
    );

    if (!response.success) throw response;

    return formatInboundFilterResponse(response.data.data);
  };

  /**
   * Api call to fetch the the Source Details.
   */
  public fetchSourceDetails = async (
    params: ISourceDetailsParams
  ): Promise<ISourceDetailsData> => {
    const response = await this.get<ISourceDetailsResponseRaw>(
      getSourceDetailsUrl(params)
    );

    if (!response.success) throw response;

    return formatSourceDetailsResponse(response.data.data);
  };

  /**
   * Api call to fetch the the Inbound Courier Provider List.
   * @returns IInboundCourierProviderResponse
   */
  public fetchInboundCourierProviderList = async () => {
    const response = await this.get<IInboundCourierProviderResponse>(
      fetchInboundCourierProviderListUrl()
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the the Inbound Types List.
   * @returns IInboundTransactionTypesResponse
   */
  public fetchInboundTypesList = async () => {
    const response = await this.get<IInboundTransactionTypesResponse>(
      fetchInboundTypesListUrl()
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to create or update the Inbound.
   * @returns ICreateOrUpdateInboundResponse
   */
  public createOrUpdateInbound = async (
    payload: ICreateOrUpdateInboundPayload
  ) => {
    const response = await this.post<ICreateOrUpdateInboundResponse>(
      fetchCreateOrUpdateInboundUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the the Inbound List Items.
   * @returns TInboundListResponse
   */
  public fetchInboundStatusList = async (
    queryParams: IFetchSearchListUrlOptions
  ) => {
    const response = await this.get<IInboundResponseRaw>(
      fetchInboundStatusListUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API method to get the current Pod information including aging list.
   *
   * @returns IPodDetailsDataResponseRaw
   */
  public getPodDetailsWithAgingInfo = async (
    queryParams: IFetchPodDetailUrlOptions
  ) => {
    const response = await this.get<IPodDetailsDataResponseRaw>(
      fetchPodDetailsWithAgingInfoUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API method to get the current Pod inventory list.
   *
   * @returns IPodInventoryListResponseRaw
   */
  public getPodInventoryListInfo = async (
    payload: IGetPodInventoryListPayload
  ) => {
    const response = await this.get<IPodInventoryListResponseRaw>(
      fetchPodInventoryListUrl(payload)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to close the Inbound Transaction.
   * @returns IInboundDetailResponseRaw
   */
  public closeInboundTransaction = async (
    payload: ICloseInboundTransactionPayload
  ) => {
    const response = await this.patch<IInboundDetailResponseRaw>(
      closeInboundTransactionUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the the Inbound History List.
   * @returns IInboundHistoryListResponseRaw
   */
  public fetchInboundHistoryList = async (
    queryParams: IGetInboundHistoryListPayload
  ) => {
    const response = await this.get<IInboundHistoryListResponseRaw>(
      inboundHistoryDetailsListInfoUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the missing inventory list.
   * @returns IInboundResponseRaw
   */
  public fetchMissingInventoryList = async (
    queryParams: IGetMissingInventoryListPayload
  ) => {
    const response = await this.get<IMissingInventoryListResponse>(
      fetchMissingInventoryListUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the field types to update for the package details.
   * @returns IFieldToUpdateTypesResponseRaw
   */
  public fetchFieldToUpdateTypesList = async () => {
    const response = await this.get<IFieldToUpdateTypesResponseRaw>(
      fetchFieldToUpdateTypesListUrl()
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to update the Inbound Package Details.
   * @returns IUpdateInboundPackageResponse
   */
  public updateInboundPackage = async (
    payload: IUpdateInboundPackagePayload
  ) => {
    const response = await this.put<IUpdateInboundPackageResponse>(
      fetchUpdateInboundPackageUrl(payload.inboundId),
      payload.requestBody
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Fetch bulk scan inventories list
   * @param payload The payload containing filter parameters
   * @returns Promise with bulk scan inventories data
   */
  public fetchBulkScanInventories = async (
    payload: IBulkScanInventoriesPayload
  ) => {
    const response = await this.get<IBulkScanInventoriesResponseRaw>(
      getBulkScanInventoriesUrl(payload)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to mark an inventory as missing.
   * @returns IMissingInventoryResponse
   */
  public markInventoryAsMissing = async (payload: IMissingInventoryPayload) => {
    const response = await this.post<IMissingInventoryResponse>(
      getMarkInventoryAsMissingUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to fetch the the Inbound List Items.
   * @returns TInboundListResponse
   */
  public fetchEvaluationList = async (
    queryParams: IFetchEvaluationListParams
  ) => {
    const response = await this.get<IEvaluationsListResponse>(
      getEvaluationsList(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the the image capture configuration associated with
   * category type.
   */
  public fetchEvaluationImageConfiguration = async (
    queryParams: IFetchEvaluationImagesConfigurationParams
  ) => {
    const response = await this.get<IEvaluationImageConfigurationResponse>(
      getEvaluationImageUploadConfigUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api Call to scan the package for creating a new evaluation session.
   */
  public scanPackageForCreatingEvaluation = async (
    payload: IScanPackageForEvaluationPayload
  ) => {
    const response = await this.post<IScanPackageForEvaluationResponse>(
      scanPackageForEvaluationUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to scan the port for creating a new evaluation
   */
  public scanPortForCreatingEvaluation = async (
    payload: IScanPortForCreatingEvaluationPayload
  ): Promise<IFormattedAssetItem> => {
    const response = await this.post<IScanPortForCreatingEvaluationResponse>(
      scanPortForEvaluationUrl(),
      payload
    );

    if (!response.success) throw response;

    return formatEvaluationsDetailsResponse(response.data.data);
  };

  /**
   * Api call to scan the port for getting info of an evaluated appraisal.
   */
  public scanEvaluatedAppraisal = async (
    payload: IScanEvaluatedAppraisalPayload
  ): Promise<IFormattedAssetItem> => {
    const response = await this.post<IScanPortForCreatingEvaluationResponse>(
      scanEvaluatedAppraisalUrl(),
      payload
    );

    if (!response.success) throw response;

    return formatEvaluationsDetailsResponse(response.data.data);
  };

  /**
   * Api call to scan the port for an existing evaluation.
   */
  public scanPortForExistingEvaluation = async (
    payload: IScanPortForExistingEvaluationPayload
  ) => {
    const response = await this.post<IScanPortForExistingEvaluationResponse>(
      scanPortForExistingEvaluationUrl(),
      payload
    );

    if (!response.success) throw response;

    return formatEvaluationsDetailsResponse(response.data.data);
  };

  /**
   * Api call to add or delete a media to an evaluation.
   */
  public addOrDeleteMediaToEvaluation = async (
    payload: IAddDeleteImageToEvaluationPayload
  ) => {
    const response = await this.post<IScanPortForCreatingEvaluationResponse>(
      addOrDeleteMediaToEvaluationUrl(),
      payload
    );

    if (!response.success) throw response;

    return formatEvaluationsDetailsResponse(response.data.data);
  };

  /**
   * Api call to close an evaluation.
   */
  public closeEvaluation = async (payload: ICloseEvaluationPayload) => {
    const response = await this.post<ICloseEvaluationResponse>(
      closeEvaluationUrl(),
      payload
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch details of an evaluation.
   */
  public fetchEvaluationDetails = async (
    payload: IFetchEvaluationDetailsParams
  ) => {
    const response = await this.get<IEvaluationDetailsResponse>(
      getEvaluationDetailsUrl(payload)
    );

    if (!response.success) throw response;

    return formatEvaluationsDetailsResponse(response.data.data);
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for All Inbound management related requests.
 */
export const INBOUND_API = InboundAPIClient.getClientInstance();
